/**
 * Poker vocabulary tags v1 — screen only (review breakdown under the card).
 *
 * One pure classifier over the existing hand payload. Judges only from the
 * recorded cards and actions: seat order, action type and board cards. Bet
 * size is never read. Anything needed that is missing → no tag (an action
 * whose seat cannot be placed stops tagging from there on). Never imported
 * by Copy / PNG / share card / `#/h/` links.
 */
import { flowPositions, postflopOrder } from '../lib/handFlow'
import type { Card, HandState, Street, StreetAction } from '../lib/types'

export type BoardTag = 'trips' | 'paired' | 'monotone' | 'twoTone' | 'rainbow'
export type ActionTag =
  | 'limp' | 'open' | 'threeBet' | 'fourBet' | 'fiveBet' | 'squeeze'
  | 'coldCall' | 'checkRaise' | 'cBet' | 'donkBet' | 'probeBet'
export type VocabTag = BoardTag | ActionTag

export const VOCAB_TAGS: readonly VocabTag[] = [
  'trips', 'paired', 'monotone', 'twoTone', 'rainbow',
  'limp', 'open', 'threeBet', 'fourBet', 'fiveBet', 'squeeze', 'coldCall', 'checkRaise', 'cBet', 'donkBet', 'probeBet',
]

/** When a line has more tags than the cap, higher priority stays. */
export const TAG_PRIORITY: Record<ActionTag, number> = {
  fiveBet: 9, fourBet: 8, threeBet: 7, squeeze: 7, checkRaise: 6,
  cBet: 5, donkBet: 5, probeBet: 5, open: 4, coldCall: 3, limp: 2,
}

/** Action tags per street line. */
export const MAX_ACTION_TAGS_PER_LINE = 2

export type VocabResult = {
  board: Partial<Record<'flop' | 'turn' | 'river', BoardTag>>
  actions: Record<string, ActionTag>
}

const POSTFLOP: Array<'flop' | 'turn' | 'river'> = ['flop', 'turn', 'river']
const TYPES = new Set(['fold', 'check', 'call', 'bet', 'raise', 'allin'])

function known(cards: Array<Card | null>): Card[] | null {
  return cards.every((c) => typeof c === 'string' && c.length === 2) ? (cards as Card[]) : null
}

/** Cards on the board as of `street` (null if any of them is missing). */
function boardThrough(hand: HandState, street: 'flop' | 'turn' | 'river'): Card[] | null {
  const parts: Array<Card | null> = [...hand.board.flop]
  if (street !== 'flop') parts.push(hand.board.turn)
  if (street === 'river') parts.push(hand.board.river)
  return known(parts)
}

export function boardTag(hand: HandState, street: 'flop' | 'turn' | 'river'): BoardTag | null {
  const cards = boardThrough(hand, street)
  if (!cards) return null
  const ranks = new Map<string, number>()
  for (const c of cards) ranks.set(c[0]!, (ranks.get(c[0]!) ?? 0) + 1)
  const top = Math.max(...ranks.values())
  if (top >= 3) return 'trips'
  if (top >= 2) return 'paired'
  if (street !== 'flop') return null
  const suits = new Set(cards.map((c) => c[1]))
  return suits.size === 1 ? 'monotone' : suits.size === 2 ? 'twoTone' : 'rainbow'
}

function seatOf(a: StreetAction, hand: HandState, seats: string[]): string | null {
  const raw = typeof a.actor === 'string' ? a.actor.trim() : ''
  if (!raw) return null
  const s = raw.toLowerCase() === 'hero' ? hand.heroPosition : raw
  return seats.find((p) => p.toLowerCase() === String(s).toLowerCase()) ?? null
}

export function classifyVocab(hand: HandState, opts: { maxPerLine?: number } = {}): VocabResult {
  const cap = opts.maxPerLine ?? MAX_ACTION_TAGS_PER_LINE
  const out: VocabResult = { board: {}, actions: {} }
  for (const st of POSTFLOP) {
    const b = boardTag(hand, st)
    if (b) out.board[st] = b
  }
  const seats = flowPositions(hand.playerCount)
  const order = postflopOrder(hand.playerCount)
  const blinds = new Set(seats.length === 2 ? ['BTN', 'BB'] : ['SB', 'BB'])
  const folded = new Set<string>()
  const tag: Record<string, ActionTag> = {}

  // Preflop raise count: the blind is the 1bet; first raise = 2bet.
  let raises = 0
  let limped = false
  let callsSinceRaise = 0
  let lastRaiser: string | null = null
  const actedPre = new Set<string>()
  // Per street state.
  let street: Street | null = null
  let checked = new Set<string>()
  let acted = new Set<string>()
  let betOpen = false
  let firstBetDone = false
  const streetActs: Record<string, StreetAction[]> = {}

  for (const a of hand.actions) {
    const seat = seatOf(a, hand, seats)
    if (!seat || !TYPES.has(a.type)) break
    if (a.street !== street) {
      street = a.street
      checked = new Set(); acted = new Set(); betOpen = street === 'preflop'; firstBetDone = false
    }
    ;(streetActs[a.street] ??= []).push(a)
    const facing = betOpen
    const isRaise = a.type === 'raise' || (a.type === 'allin' && facing)
    const isBet = a.type === 'bet' || (a.type === 'allin' && !facing)
    let t: ActionTag | null = null

    if (a.street === 'preflop') {
      if (a.type === 'call') {
        // BB never limps (its call with no raise is a check).
        if (raises === 0) { if (seat !== 'BB') { t = 'limp'; limped = true } }
        else {
          if (!blinds.has(seat) && !actedPre.has(seat)) t = 'coldCall'
          callsSinceRaise++
        }
      } else if (isRaise || a.type === 'bet') {
        raises++
        if (raises === 1) t = limped ? null : 'open'
        else if (raises === 2) t = callsSinceRaise >= 1 ? 'squeeze' : 'threeBet'
        else if (raises === 3) t = 'fourBet'
        else if (raises === 4) t = 'fiveBet'
        callsSinceRaise = 0
        lastRaiser = seat
      }
      if (!t && isRaise && checked.has(seat)) t = 'checkRaise'
      actedPre.add(seat)
    } else {
      const boardOk = boardThrough(hand, a.street) != null
      if (boardOk) {
        if (isRaise && checked.has(seat)) t = 'checkRaise'
        if (isBet && !firstBetDone && lastRaiser) {
          const raiserIn = !folded.has(lastRaiser)
          const before = order.indexOf(seat) < order.indexOf(lastRaiser) && !acted.has(lastRaiser)
          if (a.street === 'flop') {
            if (seat === lastRaiser) t = 'cBet'
            else if (raiserIn && before) t = 'donkBet'
          } else if (a.street === 'turn') {
            const flop = streetActs.flop ?? []
            const flopCheckedThrough = flop.length > 0 && flop.every((x) => x.type === 'check')
            if (flopCheckedThrough && seat !== lastRaiser && raiserIn && before) t = 'probeBet'
          }
        }
      }
      if (isBet) firstBetDone = true
    }
    if (a.type === 'check') checked.add(seat)
    if (a.type === 'fold') folded.add(seat)
    if (isBet || isRaise) betOpen = true
    acted.add(seat)
    if (t) tag[a.id] = t
  }

  // Max tags per street line: keep the highest-priority ones, show them in line order.
  for (const st of ['preflop', ...POSTFLOP] as Street[]) {
    const line = (streetActs[st] ?? []).filter((a) => tag[a.id])
    const keep = new Set(
      line
        .map((a, i) => ({ id: a.id, i, p: TAG_PRIORITY[tag[a.id]!] }))
        .sort((x, y) => y.p - x.p || x.i - y.i)
        .slice(0, cap)
        .map((x) => x.id),
    )
    for (const a of line) if (keep.has(a.id)) out.actions[a.id] = tag[a.id]!
  }
  return out
}
